<template>
    <div
        class="flex flex-col text-slate-500 text-xs"
        :class="{
            'items-end': direction === 'outbound' || scheduled,
            'items-start': direction === 'inbound',
        }"
    >
        <div
            :class="[
                'flex items-center gap-1 flex-wrap justify-end',
                {
                    'mb-1': status,
                },
            ]"
        >
            <vx-popover placement="top" arrow hover :key="createdAtKey">
                <meta-badge :icon="faClock" color="muted">
                    {{ fromNow(message.send_at || message.created_at) }}
                </meta-badge>
                <template v-slot:content>
                    <div class="text-sm py-2 px-3 whitespace-nowrap">
                        {{ toLongDateAtTime(message.send_at || message.created_at) }}
                    </div>
                </template>
            </vx-popover>

            <template v-if="direction === 'outbound'">
                <meta-badge
                    v-if="message?.origin_meta?.type === 'Message Hub'"
                    :icon="getOriginIcon(message?.origin_meta?.type)"
                    color="muted"
                >
                    {{ message?.origin_meta?.description || message?.origin_meta?.type || 'User' }}
                </meta-badge>

                <vx-popover v-else-if="message?.origin_meta?.description" placement="top" arrow hover>
                    <meta-badge :icon="getOriginIcon(message?.origin_meta?.type)" color="muted">
                        {{ message?.origin_meta?.type || 'Other' }}
                    </meta-badge>
                    <template v-slot:content>
                        <div class="text-sm py-2 px-3 whitespace-nowrap">
                            {{ message.origin_meta.description }}
                        </div>
                    </template>
                </vx-popover>
                <meta-badge v-else :icon="getOriginIcon(message?.origin_meta?.type)" color="muted">
                    {{ message?.origin_meta?.type || 'Other' }}
                </meta-badge>

                <vx-popover v-if="status" placement="top" arrow hover data-test="status-popover">
                    <meta-badge :icon="status.icon" :color="status.badgeColor" data-test="status-badge">
                        {{ status.badgeText }}
                    </meta-badge>
                    <template v-if="isImpersonating" v-slot:content>
                        <div class="rounded-lg bg-white flex flex-col gap-3">
                            <div v-if="status.detailTitle" class="flex flex-col gap-1">
                                <div class="flex items-center gap-2 font-bold text-base text-slate-700">
                                    <font-awesome-icon
                                        widthAuto
                                        :icon="faTriangleExclamation"
                                        class="text-rose-500"
                                    ></font-awesome-icon>
                                    {{ status.detailTitle }}
                                </div>
                                <div v-if="status.detailDescription" class="text-xs text-slate-700">
                                    {{ status.detailDescription }}
                                </div>
                            </div>
                            <div v-if="props.message.vendor_identity">
                                <vx-badge
                                    @click="copyText(props.message.vendor_identity)"
                                    size="xs"
                                    color="slate"
                                    data-test="vendor-identity-badge"
                                >
                                    {{ props.message.vendor_identity.slice(0, 8) }}
                                    <font-awesome-icon widthAuto :icon="faCopy"></font-awesome-icon>
                                </vx-badge>
                            </div>
                            <div
                                class="grid gap-1"
                                :class="{
                                    'grid-cols-3': messageType !== 'MMS' && props.message.num_segments,
                                    'grid-cols-2': messageType === 'MMS' || !props.message.num_segments,
                                }"
                            >
                                <div class="bg-slate-50 rounded p-4 text-center flex-1">
                                    <div class="font-bold text-sm text-slate-900">
                                        {{ messageCharCount }}
                                    </div>
                                    <div class="text-slate-500 text-xs">Characters</div>
                                </div>
                                <div
                                    v-if="props.message.num_segments && messageType !== 'MMS'"
                                    class="bg-slate-50 rounded p-4 text-center flex-1"
                                >
                                    <div class="font-bold text-sm text-slate-900">
                                        {{ props.message.num_segments }}
                                    </div>
                                    <div class="text-slate-500 text-xs">Segments</div>
                                </div>
                                <div class="bg-slate-50 rounded p-4 text-center flex-1">
                                    <div class="font-bold text-sm text-slate-900">{{ messageType }}</div>
                                    <div class="text-slate-500 text-xs">Type</div>
                                </div>
                            </div>
                        </div>
                    </template>
                </vx-popover>
            </template>
        </div>
    </div>
</template>
<script setup lang="ts">
import { VxBadge, VxPopover } from '@voxie/frontend-components';
import { toLongDateAtTime, fromNow } from '~/utils/date';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import MetaBadge from '~/components/general/MetaBadge.vue';
import { computed, type PropType } from 'vue';
import { useCopyText } from '~/composables/useCopyText';
import type { Message } from '~/types/message.types';
import {
    faCopy,
    faUser,
    faRobot,
    faMessages,
    faRocket,
    faNotebook,
    faFolderGear,
    faTriangleExclamation,
    type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import {
    faTriangleExclamation as faTriangleExclamationReg,
    faClock,
    faMailbox,
    faPaperPlane,
    faOctagonMinus,
} from '@fortawesome/pro-regular-svg-icons';
import { messageErrorDescription } from '~/utils/message';
import { useInterval } from '@vueuse/core';

const isImpersonating = Spark.isImpersonating;

const props = defineProps({
    message: {
        type: Object as PropType<Message>,
        required: true,
    },
    scheduled: {
        type: Boolean,
        default: false,
    },
});

const createdAtKey = useInterval(60 * 1000);

const direction = computed(() => props.message?.direction || 'outbound');

const getOriginIcon = (originType?: string | null): IconDefinition => {
    switch (originType) {
        case 'Message Hub':
            return faUser;
        case 'Automation':
            return faRobot;
        case 'Evergreen Campaign':
            return faMessages;
        case 'Blast Campaign':
            return faRocket;
        case 'Playbook':
            return faNotebook;
        case 'Other':
        default:
            return faFolderGear;
    }
};

const status = computed(() => {
    if (direction.value !== 'outbound') {
        return;
    }

    if (props.message.send_at) {
        return {
            badgeText: 'Scheduled',
            badgeColor: 'orange' as const,
            icon: faClock,
        };
    }

    if (!props.message.status) {
        return;
    }

    if (['queued', 'accepted', 'processing'].includes(props.message.status)) {
        return {
            badgeText: 'Sending',
            badgeColor: 'amber' as const,
            icon: faPaperPlane,
        };
    }

    if (['sent', 'expired'].includes(props.message.status)) {
        return {
            badgeText: 'Sent',
            badgeColor: 'sky' as const,
            icon: faPaperPlane,
        };
    }

    if ('delivered' === props.message.status) {
        return {
            badgeText: isImpersonating ? 'Delivered' : 'Sent',
            badgeColor: 'sky' as const,
            icon: isImpersonating ? faMailbox : faPaperPlane,
        };
    }

    const detailTitle = isImpersonating && props.message.error_code ? `Error ${props.message.error_code}` : undefined;

    const detailDescription =
        isImpersonating && props.message.error_code
            ? messageErrorDescription(props.message.vendor, props.message.error_code)
            : undefined;

    if ('undelivered' === props.message.status) {
        return {
            badgeText: 'Not Delivered',
            badgeColor: 'rose' as const,
            icon: faOctagonMinus,
            detailTitle,
            detailDescription,
        };
    }

    if ('failed' === props.message.status) {
        return {
            badgeText: 'Not Sent',
            badgeColor: 'rose' as const,
            icon: faTriangleExclamationReg,
            detailTitle,
            detailDescription,
        };
    }

    return {
        badgeText: 'Not Sent',
        badgeColor: 'rose' as const,
        icon: faTriangleExclamationReg,
        detailTitle,
        detailDescription,
    };
});

const messageCharCount = computed(() => props.message.body.length);
const messageType = computed(() => (props.message.media_url ? 'MMS' : 'SMS'));

const { copyText } = useCopyText();
</script>
